// ./src/utils/api/PageWidgetService.ts
//
// Blocks ("widgets") on an event website page — the content the CMS edits.
// Mirrors the eventsmagic-api PageWidget SDL. `where.id` accepts one id or many;
// a bulk update (>1 id) may only change status (server-enforced). `status` is the
// publish state (ACTIVE = live, DRAFT = hidden).
//
// v1 supports two block families:
//   • text  — widgetType 'html' | 'markdown', body in `content`; sub-kind + rich
//             styling in `config`.
//   • image — widgetType 'image' | 'image_gallery' | 'image_carousel'; the images
//             (host-authored) live in `config.images`. Per-row page_widget_item
//             rows stay reserved for future guest uploads.
// Shared per-block styling (title/subtitle, colours, columns) lives in `config`.
import { gql } from '@apollo/client';
import { OpResult } from '@sdflc/api-helpers';
import type { WidgetConfig, WidgetType } from '@em/blocks';

import { ApiService } from './ApiService';
import { listAllPages } from './listAllPages';

const FIELDS = `
  id
  eventPageId
  eventId
  widgetType
  title
  content
  config
  allowGuestContributions
  allowComments
  accessLevel
  accessRsvpStatuses
  accessGuestGroupIds
  accessTicketTypeIds
  visibleWhen
  orderNo
  settings
  status
  rights
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query PageWidgetList($filter: PageWidgetFilter, $params: PaginationAndSorting) {
    pageWidgetList(filter: $filter, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation PageWidgetCreate($params: PageWidgetInput) {
    pageWidgetCreate(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation PageWidgetUpdate($where: PageWidgetWhereInput, $params: PageWidgetInput) {
    pageWidgetUpdate(where: $where, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation PageWidgetRemove($where: PageWidgetWhereInput) {
    pageWidgetRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;


// The block config schema now lives in @em/blocks (shared with the shared block
// views + the SSR event website). Re-exported here so existing "@/utils/api"
// imports of WidgetConfig / WidgetType / the option unions keep working unchanged.
export type {
  WidgetType,
  ButtonVariant,
  ButtonLayout,
  WeatherMode,
  WeatherUnits,
  CountdownTarget,
  GuestSource,
  GuestLayout,
  GuestAvatarSize,
  StatSource,
  WidgetStat,
  DividerStyle,
  HighlightLayout,
  TicketLayout,
  QuoteStyle,
  WidgetCard,
  MapSource,
  EmbedAspect,
  TextBlockKind,
  ImageLayout,
  GalleryStyle,
  ImageEffect,
  WidgetImage,
  WidgetLink,
  WidgetConfig,
} from '@em/blocks';

export interface PageWidget {
  id?: string;
  eventPageId?: string | null;
  eventId?: string | null;
  widgetType?: WidgetType | string | null;
  title?: string | null;
  content?: string | null;
  config?: WidgetConfig | null;
  allowGuestContributions?: boolean | null;
  allowComments?: boolean | null;
  accessLevel?: string | null;
  accessRsvpStatuses?: string[] | null;
  accessGuestGroupIds?: string[] | null;
  accessTicketTypeIds?: string[] | null;
  /** When it shows (narrows its page's): always | before | during | after the event (null = always) — #92. */
  visibleWhen?: string | null;
  orderNo?: number | null;
  settings?: Record<string, unknown> | null;
  status?: number | null;
  rights?: string[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface PageWidgetInput {
  eventPageId?: string | null;
  widgetType?: WidgetType | string | null;
  title?: string | null;
  content?: string | null;
  config?: WidgetConfig | null;
  allowGuestContributions?: boolean | null;
  allowComments?: boolean | null;
  accessLevel?: string | null;
  accessRsvpStatuses?: string[] | null;
  accessGuestGroupIds?: string[] | null;
  accessTicketTypeIds?: string[] | null;
  /** When it shows (narrows its page's): always | before | during | after the event (null = always) — #92. */
  visibleWhen?: string | null;
  orderNo?: number | null;
  settings?: Record<string, unknown> | null;
  status?: number | null;
}

export class PageWidgetService {
  static async list(eventPageId: string, statuses?: number[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter: { eventPageId: [eventPageId], status: statuses },
    });
  }

  /**
   * Every block on every page of an event, live and draft — #62 P6(d): what the site already carries, so the wizard never
   * adds a second one. Read WHOLE (ROADMAP #118: it was one page of 500, and the wizard had to report "incomplete").
   */
  static async listForEvent(eventId: string, statuses: number[]): Promise<OpResult> {
    return listAllPages<PageWidget>(
      (filter, pagination, sortBy) => ApiService.executeQuery(LIST_QUERY, { filter, params: { pagination, sortBy } }),
      { eventId: [eventId], status: statuses },
    );
  }

  static async create(params: PageWidgetInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  /** `id` may be a single id or an array (bulk — status-only). */
  static async update(id: string | string[], params: PageWidgetInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string | string[]): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }
}
